import { Form as AntForm, Spin } from "antd";
import i18n from "i18next";
import { type FC, useCallback, useEffect, useRef, useState } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router";

import { PageLayout } from "../../../components/AppLayout/components/Content/components";
import { useLoadFormDefinitionQuery } from "../../../components/Form/services/server/serverApi";
import type { FormConfiguration, FormError, QueryParams } from "../../../components/Form/types/types";
import { showSuccessNotification, showWarningNotification } from "../../../components/NotificationAlerts";
import { canAccess } from "../../../core/router/auth/permissions";
import { BASE_PATH, FORM_ACTION } from "../../../core/router/types/routerTypes";
import { useAppDispatch } from "../../../core/utils";
import { GenericForm } from "../Form";
import { FormAction, FormFooter } from "../Form/GenericForm/FormFooter";
import { useLazyLoadGenericFormQuery, useSaveGenericFormMutation } from "../Form/GenericForm/serverApi";
import { createRequestParams, generateDefaultFormEntity, setFieldsValueByParams, setFormConfigByParams } from "./GenericFormUtils";

interface GenericFormPageProps {
    formName: string; // Specify the form name. It must match the name in basic.json definition.
    formDefaultData?: object; // if it's set then overrides form data
    resourceUrl?: string; // API endpoint to be used to manage this resource
    resourcePath?: string; // this url defines the root url for this resource. By default, it is set as resourceName.
    resourceName?: string; // It contains the name of this resource. It is mandatory when the resourceUrl is set.
    useContinue?: boolean; // If its true, when save it goes to the edit form.
    searchParams?: string; // Define url parameters to be added. It is useful when a form requires some query param to be loaded.
    backUrl?: string; // If defined it is used to navigate to when the user clicks on cancel button.
}

export const GenericFormPage: FC<GenericFormPageProps> = ({
    formName,
    formDefaultData,
    resourceName,
    resourceUrl,
    resourcePath,
    useContinue = false,
    searchParams,
    backUrl,
}) => {
    const dispatch = useAppDispatch();
    const navigate = useNavigate();
    const { id } = useParams();
    const [uriParams] = useSearchParams();

    const [formEntity, setFormEntity] = useState({});
    const [formConfig, setFormConfig] = useState<FormConfiguration>();
    const [saveForm, { isLoading: saveFormIsLoading }] = useSaveGenericFormMutation();
    const [queryParams, setQueryParams] = useState<QueryParams | null>(null);
    const [isDisabledSubmit, setIsDisabledSubmit] = useState<boolean>(true);

    const prevFormEntityRef = useRef(formEntity);

    const [form] = AntForm.useForm();
    const [loadGenericForm, { isFetching: formValueIsFetching }] = useLazyLoadGenericFormQuery();
    const { data, isFetching: configIsFetching } = useLoadFormDefinitionQuery(formName);
    const formConfigsData = data?.formConfiguration;

    useEffect(() => {
        if (formConfig) {
            const params = createRequestParams(formConfig, id, resourceUrl, resourceName);
            setQueryParams(params);
        }
    }, [formConfig, id, resourceUrl, resourceName]);

    useEffect(() => {
        if (queryParams && formConfig) {
            if (formDefaultData) {
                setFormEntity(formDefaultData);
                return;
            }

            const defaultFormEntity = generateDefaultFormEntity(formConfig);

            if (id) {
                loadGenericForm(queryParams)
                    .unwrap()
                    .then((formData) => {
                        const formValues = { ...defaultFormEntity, ...formData };
                        setFormEntity(setFieldsValueByParams(formValues, uriParams));
                    });
            } else {
                const formValues = setFieldsValueByParams(defaultFormEntity, uriParams);
                setFormEntity(formValues);
            }
        }
    }, [queryParams, loadGenericForm, setFormEntity, id, dispatch, formConfig, uriParams, formDefaultData]);

    useEffect(() => {
        if (JSON.stringify(prevFormEntityRef.current) !== JSON.stringify(formEntity)) {
            prevFormEntityRef.current = formEntity;
            setFormEntity({ ...formEntity });
        }
    }, [formEntity]);

    const enableSubmit = () => {
        setIsDisabledSubmit(false);
    };

    const canSubmit = useCallback(() => {
        return formConfig ? canAccess(formConfig.resource, "canWrite") : false;
    }, [formConfig]);
    const submitForm = useCallback(
        async (saveAndContinue: boolean) => {
            const callBackend = async () => {
                if (!queryParams) {
                    return;
                }

                const result = await saveForm({ query: queryParams, genericFormData: form.getFieldsValue() });
                const hasError = "error" in result;

                if (!hasError) {
                    dispatch(
                        showSuccessNotification({
                            text: i18n.t("common.formSaveSuccess", {
                                id: result.data.id,
                                resource: formConfig?.resource
                                    ?.replace(/-/g, " ")
                                    .replace(/s$/, "")
                                    .replace(/\b\w/g, (char) => char.toUpperCase()),
                            }),
                            autoCloseDisabled: false,
                        }),
                    );

                    if (saveAndContinue) {
                        let editRoute = `${BASE_PATH}/${resourcePath || resourceName}/${FORM_ACTION.EDIT}/${result.data.id}`;
                        if (searchParams) {
                            editRoute += `?${searchParams}`;
                        }
                        navigate(editRoute);
                    } else {
                        const back = -1;
                        if (backUrl) {
                            navigate(backUrl);
                        } else {
                            navigate(back);
                        }
                    }
                }
            };

            form.validateFields()
                .then(() => callBackend())
                .catch((err: FormError) => {
                    dispatch(
                        showWarningNotification({
                            text: `There are ${err.errorFields.length} validation errors:
                            ${err.errorFields.map((field) => `\n ${field.name.join(".")}: ${field.errors.join("; ")}`).join("")}`,
                            autoCloseDisabled: true,
                        }),
                    );
                });
        },
        [form, queryParams, saveForm, dispatch, formConfig?.resource, resourcePath, resourceName, searchParams, navigate, backUrl],
    );

    const navigateBack = useCallback(() => {
        const back = -1;
        if (backUrl) {
            navigate(backUrl);
        } else {
            navigate(back);
        }
    }, [navigate, backUrl]);

    const formActionCallback = useCallback(
        async (action: FormAction) => {
            if (action === FormAction.CANCEL) {
                navigateBack();
            } else if (action === FormAction.SUBMIT) {
                await submitForm(false);
            } else if (action === FormAction.CONTINUE) {
                await submitForm(true);
            }
        },
        [submitForm, navigateBack],
    );

    useEffect(() => {
        if (formConfigsData && !formConfig) {
            const updatedFormConfig = setFormConfigByParams(formConfigsData, uriParams);
            setFormConfig(updatedFormConfig);
        }
    }, [formConfigsData, setFormConfig, formConfig, uriParams, resourceUrl, useContinue, searchParams]);

    return (
        <PageLayout>
            <Spin spinning={formValueIsFetching || configIsFetching || saveFormIsLoading}>
                {formConfig && (
                    <>
                        <GenericForm formConfig={formConfig} onFormUpdate={enableSubmit} form={form} initialData={formEntity} />
                        <FormFooter
                            onClick={formActionCallback}
                            canSubmit={canSubmit()}
                            disabled={isDisabledSubmit}
                            useContinue={useContinue}
                        />
                    </>
                )}
            </Spin>
        </PageLayout>
    );
};
